import React, {useCallback, useEffect, useRef, useState} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCamera as faCameraRegular} from '@fortawesome/free-regular-svg-icons';
import BuildingPhotosCard from '../building/BuildingPhotosCard';
import {API_ROOT, BACKEND_ROOT} from '../../config';
import {formatDateToIso} from "../../DemandPointContext";
import {faClose, faFileDownload} from "@fortawesome/free-solid-svg-icons";


const UNIT_PHOTO_TYPES_FOR_CONTENT_CARDS = {
  PHOTO_BUILDING: "Gebäude-Fotos",
  PHOTO_CONSTRUCTION_ONT: "ONT",
  PHOTO_CONSTRUCTION_ONT_SN: "ONT SN",
  PHOTO_CONSTRUCTION_OTHER: "Sonstige",
}

const EMPTY_IMG =
    'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';

// Photos Content for individual unit - combines Building Photos + Fotos Bauausführung
const PhotosContent = ({ building, unitDocuments, setUnitDocuments }) => {
  const [documentPreviews, setDocumentPreviews] = useState({});

  const getDocumentUrl = (objectName, loadThumbnail = false, isTemp = false) => {
    return `${API_ROOT}/document?documentType=${isTemp ? "TEMP": "UNIT"}&objectName=${encodeURIComponent(objectName)}&loadThumbnail=${loadThumbnail}`;
  };

  const fetchDocumentPreview = useCallback(async (externalDocumentId) => {
    if (documentPreviews[externalDocumentId]) return;
    try {
      const url = getDocumentUrl(externalDocumentId, true);
      const token = localStorage.getItem('fastplanet_token');
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        setDocumentPreviews(prev => ({ ...prev, [externalDocumentId]: blobUrl }));
      }
    } catch (err) {
      console.error('Error fetching document preview:', err);
    }
  }, [documentPreviews]);

  useEffect(() => {
    if (unitDocuments && unitDocuments.length > 0) {
      unitDocuments.forEach(doc => {
        if (doc.externalDocumentId && !documentPreviews[doc.externalDocumentId]) {
          fetchDocumentPreview(doc.externalDocumentId);
        }
      });
    }
  }, [unitDocuments, fetchDocumentPreview, documentPreviews]);

  const onFilesUpload = async (file, docType) => {

    if (!file ) return;

    // Basic client-side checks
    if (file.size > 5 * 1024 * 1024) {
      alert('Datei ist zu groß (max. 5 MB)');
      return;
    }

    const allowed = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (allowed.indexOf(file.type) === -1) {
      if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
        alert('Nur PDF, JPG, PNG und Word Dateien sind erlaubt.');
        return;
      }
    }

    // Build FormData and POST to API
    const formDataUpload = new FormData();
    formDataUpload.append('documentType', 'UNIT');
    formDataUpload.append('displayName', file.name);
    formDataUpload.append('file', file);

    try {
      const token = localStorage.getItem('fastplanet_token');
      const response = await fetch(`${API_ROOT}/document`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formDataUpload,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const newDoc = await response.json();

      const normalizedDoc = {
        tempId: newDoc.id || crypto.randomUUID(),
        externalId: null,
        externalDocumentId: newDoc.externalDocumentId || newDoc.externalId,
        displayName: newDoc.displayName || file.name,
        fileSize: newDoc.fileSize || file.size,
        documentType: 'UNIT',
        dateOfCreation: newDoc.dateOfCreation ? formatDateToIso(newDoc.dateOfCreation) : new Date().toISOString(),
        lastModificationDate: newDoc.lastModificationDate ? formatDateToIso(newDoc.lastModificationDate) : new Date().toISOString(),
        version: 0,
        tags: newDoc.tags || [],
        unitDocumentType: docType,
        isTemp: true,
      };

      // create local preview for images FOR UPLOADED FILES ONLY
      if (file.type.startsWith('image/')) {
        const localUrl = URL.createObjectURL(file);
        setDocumentPreviews((prev) => ({ ...prev, [normalizedDoc.externalDocumentId]: localUrl }));
      }

      setUnitDocuments((prev) => [...prev, normalizedDoc]);
    } catch (err) {
      console.error('Error uploading building document:', err);
      alert('Fehler beim Hochladen des Dokuments');
    }
  };

  const downloadDocument = async (doc) => {
    try {
      let displayName = doc.displayName;
      let objectName = doc.externalDocumentId;
      const url = getDocumentUrl(objectName, false, !!doc.isTemp);
      const token = localStorage.getItem('fastplanet_token');
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = displayName || objectName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Error downloading document:', err);
      alert('Fehler beim Herunterladen des Dokuments');
    }
  };

  // Update photo filename in local state
  const updatePhotoFilename = (tempId, newFilename) => {
    setUnitDocuments(prev => {
      return prev.map(doc => {
        if (doc.tempId === tempId) {
          return {...doc, displayName: newFilename};
        }
        return doc;
      });
    });
  };

  const removePhoto = (documentId) => {
    setUnitDocuments(prev => prev.filter(doc => doc.tempId !== documentId));
  };

  return (
    <div className="space-y-6">
      {/* Building Photos Section */}
      <BuildingPhotosCard building={building} />

      {/* Fotos Bauausführung Section */}
      <div className="border rounded-lg p-4 bg-white">
        <h6 className="text-sm font-medium text-gray-700 mb-4">Fotos Bauausführung</h6>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {Object.entries(UNIT_PHOTO_TYPES_FOR_CONTENT_CARDS).map(([subKey, subLabel]) => {
            const photoDocuments = (unitDocuments || []).filter(doc => doc.unitDocumentType === subKey);
            
            return (
              <div key={subKey} className="border rounded-lg p-3 bg-gray-50">
                <h6 className="text-xs font-medium text-gray-600 mb-2 block text-center">{subLabel}</h6>
                
                {/* Photo Grid for this category */}
                <div className="space-y-2">
                  {photoDocuments.map(document => (
                    <div key={document.id} className="relative">
                      <img
                        src={documentPreviews[document?.externalDocumentId] || EMPTY_IMG}
                        alt={document.displayName}
                        className="w-full h-20 object-cover rounded border cursor-pointer hover:opacity-75 transition-opacity"
                      />
                      {document.externalDocumentId && (
                          <button
                              type="button"
                              onClick={() => downloadDocument(document)}
                              title="Foto bearbeiten"
                              className={"absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600 mr-7"}
                          >
                            <div className="absolute items-center justify-center text-white-400">
                              <FontAwesomeIcon icon={faFileDownload} size="1x" />
                            </div>
                          </button>
                      )}
                      <button
                        onClick={() => removePhoto(document.tempId)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                        title="Foto löschen"
                      >
                        <div className="absolute items-center justify-center text-white-400">
                          <FontAwesomeIcon icon={faClose} size="1x" />
                        </div>
                      </button>
                      
                      {/* Editable filename display */}
                      <div className="bottom-1 left-1 right-1 bg-black bg-opacity-50 rounded p-1">
                        <input
                          type="text"
                          value={document.displayName || 'unnamed.jpg'}
                          onChange={(e) => updatePhotoFilename(document.tempId, e.target.value)}
                          className="w-full text-xs text-white bg-transparent border-none outline-none truncate"
                          title="Klicken zum Bearbeiten des Dateinamens"
                        />
                      </div>
                      
                      <div className="text-xs text-gray-500 mt-1 truncate">
                        {new Date(document.dateOfCreation).toLocaleDateString('de-DE')}
                      </div>
                    </div>
                  ))}
                  
                  {/* Camera capture button */}
                  <button
                    onClick={() => {
                      // Create and trigger file input for unit photos
                      const fileInput = document.createElement('input');
                      fileInput.type = 'file';
                      fileInput.accept = 'image/*';
                      fileInput.capture = 'environment';
                      fileInput.style.display = 'none';
                      
                      document.body.appendChild(fileInput);
                      
                      fileInput.addEventListener('change', async function(event) {
                        await onFilesUpload(event.target.files[0], subKey)
                        document.body.removeChild(fileInput);
                      });
                      
                      fileInput.click();
                    }}
                    className="w-full h-20 border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-400 hover:bg-orange-50 transition-colors flex flex-col items-center justify-center text-gray-500"
                  >
                    <FontAwesomeIcon icon={faCameraRegular} className="text-lg mb-1" />
                    <span className="text-xs">Foto aufnehmen</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        
        {unitDocuments.filter(doc => doc.type === 'fotos_bauausfuehrung').length === 0 && (
          <div className="text-center py-4 text-gray-500">
            <p className="text-sm">Noch keine Fotos der Bauausführung vorhanden</p>
            <p className="text-xs mt-1">Verwenden Sie die Kamera-Buttons in den Kategorien oben</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotosContent;
